/**
 * Connections API Service
 * Handles Plaid/Akoya connection management
 * Based on SPECIFICATION.md Section 7.5
 */
import api from './api';
import type {
  Connection,
  ConnectionStatus,
  ConnectionProvider,
  LinkToken,
  ExchangeTokenRequest,
  ExchangeTokenResponse,
  ConnectionDetail,
  SyncedAccountDetail,
  SyncHistoryEntry,
  ForceRefreshResult,
  AccountVisibilityUpdate,
  SyncHistoryStatus,
  SyncType,
  AccountSyncStatus,
} from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface ConnectionWithAccounts extends Connection {
  accounts: ConnectionAccount[];
}

export interface ConnectionAccount {
  id: string;
  name: string;
  officialName?: string;
  type: string;
  subtype?: string;
  mask?: string;
  balance: number;
  currency: string;
  isHidden: boolean;
}

// ============================================================================
// Transformation Helpers
// ============================================================================

/**
 * Transform connection response from backend.
 * Backend now returns camelCase, so we just validate and apply defaults.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformConnection = (conn: any): Connection => ({
  id: conn.id,
  tenantId: conn.tenantId ?? conn.tenant_id,
  provider: conn.provider,
  institutionId: conn.institutionId ?? conn.institution_id,
  institutionName: conn.institutionName ?? conn.institution_name,
  status: conn.status,
  accountsCount: conn.accountsCount ?? conn.accounts_count ?? 0,
  lastSyncAt: conn.lastSyncAt ?? conn.lastSyncedAt ?? conn.last_synced_at ?? conn.last_sync_at ?? null,
  errorCode: conn.errorCode ?? conn.error_code ?? conn.lastErrorCode ?? conn.last_error_code,
  errorMessage: conn.errorMessage ?? conn.error_message ?? conn.lastError ?? conn.last_error,
  createdAt: conn.createdAt ?? conn.created_at,
  updatedAt: conn.updatedAt ?? conn.updated_at,
});

/**
 * Transform account response from backend.
 * Backend now returns camelCase. Still need to parse Decimal strings to numbers.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformAccount = (account: any): ConnectionAccount => {
  // Backend sends Decimal values as strings to preserve precision
  // We need to parse them as numbers for frontend calculations
  const rawBalance = account.balanceCurrent ?? account.balance_current ?? account.balance ?? account.currentBalance ?? account.current_balance;
  const balance = rawBalance !== null && rawBalance !== undefined
    ? Number(rawBalance)
    : 0;

  return {
    id: account.id,
    name: account.name,
    officialName: account.officialName ?? account.official_name,
    type: account.accountType ?? account.account_type ?? account.type ?? 'other',
    subtype: account.accountSubtype ?? account.account_subtype ?? account.subtype,
    mask: account.mask,
    // Ensure balance is a valid number (NaN becomes 0)
    balance: Number.isNaN(balance) ? 0 : balance,
    currency: account.currency ?? 'USD',
    isHidden: account.isHidden ?? account.is_hidden ?? false,
  };
};

/**
 * Transform sync history entry from backend.
 * Field names: startedAt, completedAt, syncType, transactionsAdded, etc.
 * Schema source: services/aggregator/app/schemas/connection.py lines 229-249
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformSyncHistoryEntry = (entry: any): SyncHistoryEntry => ({
  id: entry.id,
  startedAt: entry.startedAt ?? entry.started_at,
  completedAt: entry.completedAt ?? entry.completed_at ?? null,
  status: (entry.status as SyncHistoryStatus) ?? 'failed',
  syncType: (entry.syncType ?? entry.sync_type) as SyncType,
  transactionsAdded: entry.transactionsAdded ?? entry.transactions_added ?? 0,
  transactionsModified: entry.transactionsModified ?? entry.transactions_modified ?? 0,
  transactionsRemoved: entry.transactionsRemoved ?? entry.transactions_removed ?? 0,
  accountsSynced: entry.accountsSynced ?? entry.accounts_synced ?? 0,
  errorMessage: entry.errorMessage ?? entry.error_message ?? null,
  errorCode: entry.errorCode ?? entry.error_code ?? null,
});

/**
 * Transform synced account detail from backend.
 * CRITICAL: Use exact camelCase field names from backend serialization.
 * - providerAccountId (NOT plaidAccountId)
 * - coreAccountId (NOT accountId)
 * - accountType (NOT type)
 * - balanceCurrent (NOT currentBalance)
 * Schema source: services/aggregator/app/schemas/account_sync.py lines 20-56
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformSyncedAccount = (account: any): SyncedAccountDetail => {
  // Parse Decimal strings to numbers
  const rawCurrent = account.balanceCurrent ?? account.balance_current;
  const rawAvailable = account.balanceAvailable ?? account.balance_available;

  const balanceCurrent = rawCurrent !== null && rawCurrent !== undefined
    ? Number(rawCurrent)
    : null;
  const balanceAvailable = rawAvailable !== null && rawAvailable !== undefined
    ? Number(rawAvailable)
    : null;

  return {
    id: account.id,
    connectionId: account.connectionId ?? account.connection_id,
    providerAccountId: account.providerAccountId ?? account.provider_account_id,
    coreAccountId: account.coreAccountId ?? account.core_account_id ?? null,
    accountType: account.accountType ?? account.account_type ?? 'other',
    accountSubtype: account.accountSubtype ?? account.account_subtype ?? null,
    name: account.name,
    mask: account.mask ?? null,
    balanceCurrent: balanceCurrent !== null && !Number.isNaN(balanceCurrent) ? balanceCurrent : null,
    balanceAvailable: balanceAvailable !== null && !Number.isNaN(balanceAvailable) ? balanceAvailable : null,
    currency: account.currency ?? 'USD',
    syncStatus: (account.syncStatus ?? account.sync_status ?? 'pending') as AccountSyncStatus,
    lastSyncedAt: account.lastSyncedAt ?? account.last_synced_at ?? null,
    isHidden: account.isHidden ?? account.is_hidden ?? false,
  };
};

/**
 * Transform connection detail response from backend.
 * Extends Connection with sync history and diagnostic info.
 * Schema source: services/aggregator/app/schemas/connection.py lines 251-308
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformConnectionDetail = (conn: any): ConnectionDetail => {
  // Start with base connection fields
  const base = transformConnection(conn);

  return {
    ...base,
    // Error information
    lastError: conn.lastError ?? conn.last_error ?? null,
    lastErrorAt: conn.lastErrorAt ?? conn.last_error_at ?? null,
    lastErrorCode: conn.lastErrorCode ?? conn.last_error_code ?? null,
    // Sync state
    initialSyncComplete: conn.initialSyncComplete ?? conn.initial_sync_complete ?? false,
    // Consent management
    consentExpiresAt: conn.consentExpiresAt ?? conn.consent_expires_at ?? null,
    // Sync history (transform each entry)
    syncHistory: (conn.syncHistory ?? conn.sync_history ?? []).map(transformSyncHistoryEntry),
    // Accounts list (transform each account with detailed fields)
    accounts: (conn.accounts ?? []).map(transformSyncedAccount),
  };
};

/**
 * Transform force refresh result from backend.
 * Schema source: services/aggregator/app/schemas/connection.py lines 146-171
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformForceRefreshResult = (result: any): ForceRefreshResult => ({
  connectionId: result.connectionId ?? result.connection_id,
  success: result.success ?? false,
  accountsSynced: result.accountsSynced ?? result.accounts_synced ?? 0,
  transactionsSynced: result.transactionsSynced ?? result.transactions_synced ?? 0,
  holdingsSynced: result.holdingsSynced ?? result.holdings_synced ?? 0,
  syncedAt: result.syncedAt ?? result.synced_at ?? new Date().toISOString(),
  error: result.error ?? null,
});

export interface ConnectionListParams {
  status?: ConnectionStatus;
  provider?: ConnectionProvider;
  includeAccounts?: boolean;
}

export interface ConnectionsListResponse {
  connections: Connection[];
}

export interface LinkTokenCreateParams {
  products?: string[];
  countryCodes?: string[];
  language?: string;
  institutionId?: string;
  redirectUri?: string;
}

export interface SyncStatus {
  connectionId: string;
  status: 'idle' | 'syncing' | 'completed' | 'failed';
  lastSyncAt?: string;
  nextSyncAt?: string;
  progress?: {
    current: number;
    total: number;
    stage: 'accounts' | 'transactions' | 'balances';
  };
  error?: string;
}

export interface SyncResult {
  connectionId: string;
  success: boolean;
  syncedAt: string;
  accountsUpdated: number;
  transactionsAdded: number;
  transactionsModified: number;
  transactionsRemoved: number;
  error?: string;
}

export interface ForceSyncRequest {
  fullRefresh?: boolean;
  startDate?: string;
}

export interface RelinkResponse {
  linkToken: string;
  expiration: string;
}

export interface Institution {
  id: string;
  name: string;
  logo?: string;
  primaryColor?: string;
  url?: string;
  provider: ConnectionProvider;
  products: string[];
  countryCodes: string[];
}

export interface InstitutionSearchResult {
  institutions: Institution[];
  total: number;
}

// ============================================================================
// Connections Service
// ============================================================================

export const connectionsService = {
  /**
   * Get all connections for the current tenant
   */
  getAll: async (params: ConnectionListParams = {}): Promise<Connection[]> => {
    const queryParams: Record<string, unknown> = {};

    if (params.status) queryParams.status = params.status;
    if (params.provider) queryParams.provider = params.provider;
    if (params.includeAccounts !== undefined) queryParams.includeAccounts = params.includeAccounts;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<{ connections: any[] }>('/connections', queryParams);
    return response.connections.map(transformConnection);
  },

  /**
   * Get a single connection by ID
   */
  getById: async (id: string): Promise<ConnectionWithAccounts> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/connections/${id}`);
    const connection = transformConnection(response) as ConnectionWithAccounts;
    if (response.accounts) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      connection.accounts = response.accounts.map((acc: any) => transformAccount(acc));
    } else {
      connection.accounts = [];
    }
    return connection;
  },

  /**
   * Get accounts for a specific connection
   */
  getAccounts: async (connectionId: string): Promise<ConnectionAccount[]> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<{ accounts: any[] }>(`/connections/${connectionId}/accounts`);
    return response.accounts.map(transformAccount);
  },

  /**
   * Delete a connection (removes all associated accounts and transactions)
   */
  delete: async (id: string): Promise<void> => {
    return api.delete(`/connections/${id}`);
  },

  // ============================================================================
  // Plaid Link Integration
  // ============================================================================

  /**
   * Create a Plaid Link token for new connection
   * Calls POST /connections/link-token with provider='plaid'
   *
   * WP-CONN-1: Backend now handles product selection - always requests
   * transactions + investments consent. Products param is deprecated.
   */
  createPlaidLinkToken: async (params: LinkTokenCreateParams = {}): Promise<LinkToken> => {
    const payload = {
      provider: 'plaid',
      redirectUri: params.redirectUri,
      // WP-CONN-1: products ignored - backend handles product selection
    };
    const response = await api.post<{
      linkToken: string;
      expiration: string;
      provider: string;
    }>('/connections/link-token', payload);

    return {
      linkToken: response.linkToken,
      expiration: response.expiration,
      requestId: '', // Backend doesn't return requestId, but type expects it
    };
  },

  /**
   * Exchange Plaid public token after successful Link
   * Calls POST /connections/exchange with provider='plaid'
   */
  exchangePlaidToken: async (data: ExchangeTokenRequest): Promise<ExchangeTokenResponse> => {
    const payload = {
      publicToken: data.publicToken,
      provider: 'plaid',
      institutionId: data.institutionId,
    };
    const response = await api.post<{
      id: string;
      provider: string;
      institutionName: string;
      institutionId: string;
      status: string;
      accountsCount: number;
      createdAt: string;
      lastSyncedAt: string | null;
    }>('/connections/exchange', payload);

    return {
      connectionId: response.id,
      status: response.status as ConnectionStatus,
      accountsCount: response.accountsCount,
    };
  },

  /**
   * Get relink token for updating credentials
   */
  getRelinkToken: async (connectionId: string): Promise<RelinkResponse> => {
    return api.post<RelinkResponse>(`/connections/${connectionId}/relink`);
  },

  // ============================================================================
  // Akoya Integration
  // ============================================================================

  /**
   * Create an Akoya link token
   * Calls POST /connections/link-token with provider='akoya'
   */
  createAkoyaLinkToken: async (params: LinkTokenCreateParams = {}): Promise<LinkToken> => {
    const payload = {
      provider: 'akoya',
      redirectUri: params.redirectUri,
    };
    const response = await api.post<{
      linkToken: string;
      expiration: string;
      provider: string;
    }>('/connections/link-token', payload);

    return {
      linkToken: response.linkToken,
      expiration: response.expiration,
      requestId: '', // Backend doesn't return requestId, but type expects it
    };
  },

  /**
   * Exchange Akoya token
   * Calls POST /connections/exchange with provider='akoya'
   */
  exchangeAkoyaToken: async (data: ExchangeTokenRequest): Promise<ExchangeTokenResponse> => {
    const payload = {
      publicToken: data.publicToken,
      provider: 'akoya',
      institutionId: data.institutionId,
    };
    const response = await api.post<{
      id: string;
      provider: string;
      institutionName: string;
      institutionId: string;
      status: string;
      accountsCount: number;
      createdAt: string;
      lastSyncedAt: string | null;
    }>('/connections/exchange', payload);

    return {
      connectionId: response.id,
      status: response.status as ConnectionStatus,
      accountsCount: response.accountsCount,
    };
  },

  // ============================================================================
  // Sync Operations
  // ============================================================================

  /**
   * Get sync status for a connection
   */
  getSyncStatus: async (connectionId: string): Promise<SyncStatus> => {
    return api.get<SyncStatus>(`/connections/${connectionId}/sync/status`);
  },

  /**
   * Force sync for a connection
   */
  forceSync: async (connectionId: string, params: ForceSyncRequest = {}): Promise<SyncResult> => {
    interface ApiSyncResult {
      connectionId: string;
      success: boolean;
      accountsSynced: number;
      transactionsSynced: number;
      syncedAt: string;
      error?: string;
    }
    const result = await api.post<ApiSyncResult>(`/connections/${connectionId}/sync`, params);
    return {
      connectionId: result.connectionId,
      success: result.success,
      syncedAt: result.syncedAt,
      accountsUpdated: result.accountsSynced || 0,
      transactionsAdded: result.transactionsSynced || 0,
      transactionsModified: 0,
      transactionsRemoved: 0,
      error: result.error,
    };
  },

  /**
   * Sync all connections
   */
  syncAll: async (): Promise<{ syncedCount: number; results: SyncResult[] }> => {
    interface ApiSyncResult {
      connectionId: string;
      success: boolean;
      accountsSynced: number;
      transactionsSynced: number;
      syncedAt: string;
      error?: string;
    }
    interface ApiSyncAllResult {
      syncedCount: number;
      results: ApiSyncResult[];
    }
    const result = await api.post<ApiSyncAllResult>('/connections/sync-all');
    return {
      syncedCount: result.syncedCount,
      results: (result.results || []).map((r) => ({
        connectionId: r.connectionId,
        success: r.success,
        syncedAt: r.syncedAt,
        accountsUpdated: r.accountsSynced || 0,
        transactionsAdded: r.transactionsSynced || 0,
        transactionsModified: 0,
        transactionsRemoved: 0,
        error: r.error,
      })),
    };
  },

  // ============================================================================
  // Institution Search
  // ============================================================================

  /**
   * Search for institutions by name
   */
  searchInstitutions: async (
    query: string,
    params: {
      provider?: ConnectionProvider;
      countryCodes?: string[];
      limit?: number;
    } = {}
  ): Promise<InstitutionSearchResult> => {
    const queryParams: Record<string, unknown> = {
      query,
    };

    if (params.provider) queryParams.provider = params.provider;
    if (params.countryCodes?.length) queryParams.countryCodes = params.countryCodes.join(',');
    if (params.limit !== undefined) queryParams.limit = params.limit;

    return api.get<InstitutionSearchResult>('/connections/institutions/search', queryParams);
  },

  /**
   * Get institution by ID
   */
  getInstitution: async (institutionId: string): Promise<Institution> => {
    return api.get<Institution>(`/connections/institutions/${institutionId}`);
  },

  // ============================================================================
  // Connection Details & Maintenance (WP-FRONTEND-CONN-1)
  // ============================================================================

  /**
   * Get detailed connection info including sync history and accounts.
   * Used for the ConnectionDetailsModal.
   *
   * @param connectionId - UUID of the connection
   * @returns ConnectionDetail with sync history, error info, and accounts
   */
  getConnectionDetails: async (connectionId: string): Promise<ConnectionDetail> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/connections/${connectionId}/details`);
    return transformConnectionDetail(response);
  },

  /**
   * Force refresh a connection - clears sync cursor and re-fetches all data.
   * This is a heavy operation that should be rate-limited (2/minute on backend).
   *
   * @param connectionId - UUID of the connection to refresh
   * @returns ForceRefreshResult with sync statistics
   */
  forceRefresh: async (connectionId: string): Promise<ForceRefreshResult> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(`/connections/${connectionId}/force-refresh`);
    return transformForceRefreshResult(response);
  },

  /**
   * Update account visibility (hide/show).
   * Hidden accounts are excluded from balance calculations and Sankey diagrams.
   *
   * @param connectionId - UUID of the connection
   * @param accountId - UUID of the synced account
   * @param data - { isHidden: boolean }
   * @returns Updated SyncedAccountDetail
   */
  updateAccountVisibility: async (
    connectionId: string,
    accountId: string,
    data: AccountVisibilityUpdate
  ): Promise<SyncedAccountDetail> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.patch<any>(
      `/connections/${connectionId}/accounts/${accountId}`,
      data
    );
    return transformSyncedAccount(response);
  },

  /**
   * Hide or show a connected account addressed by its CORE account id
   * (Codex review on PR #8).
   *
   * Core's accounts.connectionId is core's financial_connections id, not the
   * aggregator connection id updateAccountVisibility needs, so the accounts
   * page uses this instead. The aggregator updates every synced row linked
   * to the core account and propagates to core.
   *
   * Rejects with ApiClientError status 404, code SYNCED_ACCOUNT_NOT_FOUND
   * when no synced row is linked (e.g. a manual account).
   */
  updateVisibilityByCoreAccount: async (
    coreAccountId: string,
    data: AccountVisibilityUpdate
  ): Promise<void> => {
    await api.patch<unknown>(`/connections/core-accounts/${coreAccountId}/visibility`, data);
  },

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get connections that need attention (errors, pending reauth)
   */
  getConnectionsNeedingAttention: async (): Promise<Connection[]> => {
    const connections = await connectionsService.getAll();
    return connections.filter(
      (conn) => conn.status === 'error' || conn.status === 'pending_reauth'
    );
  },

  /**
   * Get active connections only
   */
  getActiveConnections: async (): Promise<Connection[]> => {
    return connectionsService.getAll({ status: 'active' });
  },

  /**
   * Check if user has any connected accounts
   */
  hasConnections: async (): Promise<boolean> => {
    const connections = await connectionsService.getAll();
    return connections.length > 0;
  },

  /**
   * Get connection count by status
   */
  getConnectionCounts: async (): Promise<Record<ConnectionStatus, number>> => {
    const connections = await connectionsService.getAll();

    const counts: Record<ConnectionStatus, number> = {
      active: 0,
      pending: 0,
      error: 0,
      pending_reauth: 0,
      disconnected: 0,
    };

    connections.forEach((conn) => {
      counts[conn.status] = (counts[conn.status] || 0) + 1;
    });

    return counts;
  },

  /**
   * Get all connections with their accounts
   */
  getAllWithAccounts: async (): Promise<ConnectionWithAccounts[]> => {
    const connections = await connectionsService.getAll({ includeAccounts: true });
    return connections as ConnectionWithAccounts[];
  },

  /**
   * Get connection by institution ID
   */
  getByInstitutionId: async (institutionId: string): Promise<Connection | null> => {
    const connections = await connectionsService.getAll();
    return connections.find((conn) => conn.institutionId === institutionId) || null;
  },

  /**
   * Get last sync time across all connections
   */
  getLastSyncTime: async (): Promise<string | null> => {
    const connections = await connectionsService.getAll();

    const syncTimes = connections
      .filter((conn) => conn.lastSyncAt)
      .map((conn) => new Date(conn.lastSyncAt!).getTime());

    if (syncTimes.length === 0) return null;

    const latestSync = Math.max(...syncTimes);
    return new Date(latestSync).toISOString();
  },

  /**
   * Refresh connection (alias for relink flow)
   */
  refreshConnection: async (connectionId: string): Promise<RelinkResponse> => {
    return connectionsService.getRelinkToken(connectionId);
  },
};

// Legacy API export for backward compatibility
export const connectionsApi = connectionsService;

export default connectionsService;
